import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { Config } from 'app/shared/services/config';
import { HttpClient } from '@angular/common/http';
import { Units, ApiResponse, EntityUnit, DisplayGroupModel } from '../models/units';
import { CalculatedBaseEntity, CalculatedEntitiesRequestRow } from 'app/pages/view-data/calculated-entity/calculated-entity.model';
import { map } from 'rxjs/operators';

interface ValidateEquationRequest {
    expression: string,
    variables: string[],
}

@Injectable()
export class CalculatedEntitiesService {

    constructor(private http: HttpClient) {}

    public getUnits(): Observable<Array<Units>> {
        const getAllUnits = Config.getUrl(Config.urls.getUnits);
        return this.http
            .get<ApiResponse<Array<Units>>>(getAllUnits)
            .pipe(map((response: ApiResponse<Array<Units>>) => response.payload));
    }

    public getPreDefinedEntities(customerID: number): Observable<Array<EntityUnit>> {
        const getEntitiesExceptCE = Config.getUrl(Config.urls.getPreDefinedEntitiesOnly);
        return this.http
            .get<ApiResponse<Array<EntityUnit>>>(`${getEntitiesExceptCE}?cid=${customerID}`)
            .pipe(map((response: ApiResponse<Array<EntityUnit>>) => response.payload));
    }

    public getDisplayGroupsByUnitId(unitId: number): Observable<Array<DisplayGroupModel>> {
        const getDisplayGroups = Config.getUrl(Config.urls.getDisplayGroups);
        return this.http
            .get<ApiResponse<Array<DisplayGroupModel>>>(getDisplayGroups + `?unitId=${unitId}`)
            .pipe(map((response: ApiResponse<Array<DisplayGroupModel>>) => response.payload));
    }

    public validateEquation(equation: string, equationVariable: string[]) {
        const params: ValidateEquationRequest = {
            expression: equation,
            variables: equationVariable,
        };

        const parseEquationURI = Config.getUrl(Config.urls.parseEquation);
        return this.http.post<ApiResponse<void>>(parseEquationURI, params);
    }

    public saveCalculatedEntityWithEquation(
        validatedEntity: CalculatedBaseEntity,
        customerId: number,
        locationId: number,
    ): Observable<ApiResponse<number>> {
        const parseEquationURI = Config.getUrl(
            Config.urls.createCalculatedEntity + `?cid=${customerId}` + `&lid=${locationId}`,
        );
        return this.http.post<ApiResponse<number>>(parseEquationURI, validatedEntity);
    }

    public getCalculatedEntitiesDetails(
        cid: number,
        lid: number,
        startPage?: number,
        pageSize?: number,
        serachValue?: string,
    ): Observable<Array<CalculatedEntitiesRequestRow>> {
        const url = '&PageSize=' + pageSize + '&StartPage=' + startPage;
        let postFixUrl;
        serachValue ? (postFixUrl = '&SearchValue=' + serachValue) : (postFixUrl = '');
        const getAllcalculatedEntities = Config.getUrl(Config.urls.getCalculatedEntities);
        return this.http
            .get<ApiResponse<CalculatedEntitiesRequestRow[]>>(getAllcalculatedEntities + `?cid=${cid}` + `&lid=${lid}` + url + postFixUrl)
            .pipe(map((response) => response.payload));
    }

    public getCalculatedEntitiesById(ceid: number, cid: number, lid: number): Observable<CalculatedBaseEntity> {
        const getcalculatedEntityById = Config.getUrl(Config.urls.getCalculatedEntityById);
        return this.http
            .get<ApiResponse<CalculatedBaseEntity>>(getcalculatedEntityById + `?ceid=${ceid}` + `&cid=${cid}` + `&lid=${lid}`)
            .pipe(map((response) => response.payload));
    }

    public updateCalculatedEntity(
        validatedEntity: CalculatedBaseEntity,
        customerId: number,
        locationId: number,
    ): Observable<void> {
        const updatecalculatedBaseURI = Config.getUrl(Config.urls.updateCalculatedEntity);
        const updateCalcEntityURI = updatecalculatedBaseURI + `?cid=${customerId}` + `&lid=${locationId}`;
        return this.http.put<void>(updateCalcEntityURI, validatedEntity);
    }

    public mapEntityToLocation(ceid: number, cid: number, lid: number, newlid: number): Observable<void> {
        const mapcalculatedEntityByLocationId = Config.getUrl(Config.urls.mapCalculatedEntityByLocation);
        return this.http
            .put<void>(
                mapcalculatedEntityByLocationId + `?ceid=${ceid}` + `&cid=${cid}` + `&lid=${lid}` + `&newlid=${newlid}`,
                null,
            );
    }

    public deleteCalculatedEntity(
        calculatedEntityId: number,
        locationId: number,
        customerId: number,
    ): Observable<boolean> {
        return this.http.delete<boolean>(
            Config.getUrl(Config.urls.deleteCalculatedEntity) +
                `?ceid=${calculatedEntityId}` +
                `&lid=${locationId}` +
                `&cid=${customerId}`,
        );
    }
}
